import React from "react";
import { Card, Col, Row, Typography, Button, Flex } from "antd";
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  RiseOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import LeadReports from "./LeadReports";
import ConversionReport from "./ConversionReport";
import EmployeeActivityReport from "./EmployeeActivityReport";
import TaskReports from "./TaskReports";
import DealReports from "./DealReports";
import LeadStatusChangeReport from "./LeadStatusChangeReport";

import "./style.css";

const { Title, Text } = Typography;

const REPORTS = [
  {
    key: "lead-status",
    title: "Lead Status Count Report",
    description: "Lead counts grouped by source, status, or purpose.",
    icon: <BarChartOutlined style={{ fontSize: 28 }} />,
    component: LeadReports,
  },
  {
    key: "lead-status-changes",
    title: "Lead Status Change Report",
    description: "Leads whose status changed to a selected stage during a date range, regardless of when they were created.",
    icon: <SwapOutlined style={{ fontSize: 28 }} />,
    component: LeadStatusChangeReport,
  },
  {
    key: "conversion",
    title: "Lead Conversion Report",
    description: "Conversion rate by employee or source for a date range.",
    icon: <RiseOutlined style={{ fontSize: 28 }} />,
    component: ConversionReport,
  },
  {
    key: "employee-activity",
    title: "Employee Activity & Calls",
    description: "Calls logged, leads touched, and field edits per employee.",
    icon: <PhoneOutlined style={{ fontSize: 28 }} />,
    component: EmployeeActivityReport,
  },
  {
    key: "tasks",
    title: "Follow-up / Overdue Tasks",
    description: "Overdue, due-today, and upcoming tasks across the team.",
    icon: <ClockCircleOutlined style={{ fontSize: 28 }} />,
    component: TaskReports,
  },
  {
    key: "deals",
    title: "Deal & Sales Performance",
    description: "Deal value, win rate, and pipeline by stage and employee.",
    icon: <DollarOutlined style={{ fontSize: 28 }} />,
    component: DealReports,
  },  
];

const CrmReport = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = searchParams.get("report");
  const active = REPORTS.find((r) => r.key === activeKey);

  const openReport = (key) => setSearchParams({ report: key });
  const backToCatalog = () => setSearchParams({});

  const ActiveComponent = active?.component;

  return (
    <div className="p-4">
      <Card
        className="shadow-md rounded-xl"
        title={
          <Flex align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
              onClick={active ? backToCatalog : () => navigate(-1)}
              style={{ marginRight: 8, padding: 0, height: "auto", lineHeight: "1", color: "#000" }}
              data-testid="crm-reports-back-button"
            />
            <Title id="crmReportTitle" level={4} style={{ margin: 0 }}>
              {active ? active.title : "CRM Reports"}
            </Title>
          </Flex>
        }
        styles={{
          header: { padding: "12px 16px" },
          body: { paddingTop: active ? 0 : 16 },
        }}
      >
        {active ? (
          <ActiveComponent />
        ) : (
          <Row gutter={[16, 16]}>
            {REPORTS.map((report) => (
              <Col xs={24} sm={12} lg={8} key={report.key}>
                <Card
                  hoverable
                  className="crm-report-card"
                  onClick={() => openReport(report.key)}
                >
                  <Flex gap={16} align="flex-start">
                    <div className="crm-report-card-icon">{report.icon}</div>
                    <div>
                      <Title level={5} style={{ marginBottom: 4 }}>
                        {report.title}
                      </Title>
                      <Text type="secondary">{report.description}</Text>
                    </div>
                  </Flex>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
};

export default CrmReport;
